import { CheckIcon } from "lucide-react";
import type React from "react";
import { cn } from "@/lib/utils";

interface CircularProgressProps {
  value: number;
  size?: number;
  strokeWidth?: number;
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
  value,
  size = 64,
  strokeWidth = 6,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  const isComplete = value >= 100;
  const isLoading = value === 0;

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg
        aria-hidden="true"
        className={cn(isLoading ? "animate-spin" : "")}
        height={size}
        style={{ transformOrigin: "center" }}
        width={size}
      >
        {!isComplete && (
          <>
            <circle
              cx={size / 2}
              cy={size / 2}
              fill="transparent"
              r={radius}
              stroke="#cfcfcf"
              strokeWidth={strokeWidth}
            />
            <circle
              className="transition-all duration-200"
              cx={size / 2}
              cy={size / 2}
              fill="transparent"
              r={radius}
              stroke="#3b82f6"
              strokeDasharray={circumference}
              strokeDashoffset={isLoading ? circumference * 0.75 : offset}
              strokeLinecap="round"
              strokeWidth={strokeWidth}
            />
          </>
        )}
      </svg>

      <div
        className={`absolute flex items-center justify-center rounded-full ${
          isComplete ? "bg-success" : ""
        }`}
        style={{
          width: size - strokeWidth * 2,
          height: size - strokeWidth * 2,
        }}
      >
        {isComplete ? (
          <CheckIcon
            className="text-white w-4 h-4 z-10"
            data-testid="check-icon"
            strokeWidth={3}
          />
        ) : (
          <span className="text-xs font-semibold text-primary z-10">
            {value}%
          </span>
        )}
      </div>
    </div>
  );
};
